import queryString from 'query-string';
import { analyticsService } from '@common/analytics';
import { getAdditionalPurchaseInfoFromURL } from '@common/api/api';
import { payPalProcessingFailReason } from '../../config/util';
import {
  paramOrNul, toLowerCaseSpaced, paymentKindToAnalyticsName, toCamelCase, getStartChatClickSource, fromMobileApp
} from '../../../common/config/utils';
import Const from '../../config/const';
import { purchaseDetailsTypes, topupFlows } from '../../../common/config/const';
import {
  getDestination,
  aditionalPurchaseProps,
  getContinueSessionValue
} from '../../actions/analytics';
import { log, LOG_LEVELS } from '../../../common/config/app_logger';
import { appRoutes } from '../../config/app_routes';
import { getAdvisorSlug } from '../../project_items/helper_functions';

export const endPurchaseErrorTypes = [
  'advisor_busy',
  'advisor_offline',
  'spending_limits'
];

export const isSideMenu = ({ type }) => (type === purchaseDetailsTypes.SIDE_MENU);

export const isAffiliatePromoPopup = ({ type }) => (
  type === purchaseDetailsTypes.AFFILIATE_PROMO_POPUP
);

export const sign = ({ URL }) => (URL.indexOf('?') === -1 ? '?' : '&');

export const getFollowupParam = ({ isContinue, type, topupFlow }) => {
  let { context } = toCamelCase(queryString.parse(window.location.search));
  if (context) return { context };

  const getContext = (suffix, originalValue) => {
    if (!isContinue) return originalValue;
    if (topupFlow === topupFlows.quick) return `quick_${ suffix }`;
    return `regular_${ suffix }`;
  };

  switch (type) {
    case purchaseDetailsTypes.CHAT: {
      context = getContext('topup_chat', 'live_chat');
      break;
    }
    case purchaseDetailsTypes.VIDEO_CHAT: {
      context = getContext('topup_video', 'video_call');
      break;
    }
    case purchaseDetailsTypes.VOICE_CHAT: {
      context = getContext('topup_voice', 'voice_call');
      break;
    }
    default: {
      context = 'credit_screen';
      break;
    }
  }
  return { context };
};

export const renderButtonTitle = ({ isContinue, type, t }) => {
  if (isContinue) {
    return t('general.continue');
  }
  switch (type) {
    case purchaseDetailsTypes.CHAT:
      return t('chat.start_chat');
    case purchaseDetailsTypes.VIDEO_CHAT:
      return t('chat.start_video_call');
    case purchaseDetailsTypes.VOICE_CHAT:
      return t('chat.start_voice_call');
    case purchaseDetailsTypes.AFFILIATE_PROMO_POPUP:
      return t('purchase.affiliate_promo_popup_confirm_button');
    default:
      return 'OK';
  }
};

export const selectCloseButtonActionIfNeed = ({
  type, cancelChatOnPP, hideAffiliatePromoPopup
}) => {
  if (type !== purchaseDetailsTypes.SIDE_MENU
    && type !== purchaseDetailsTypes.AFFILIATE_PROMO_POPUP
    && type !== purchaseDetailsTypes.ADVISOR_BONUS) {
    if (cancelChatOnPP) cancelChatOnPP();
  }
  if (type === purchaseDetailsTypes.AFFILIATE_PROMO_POPUP) {
    if (hideAffiliatePromoPopup) hideAffiliatePromoPopup();
  }
};

const paymentOptionEventData = ({ currentPaymentOption }) => {
  if (!currentPaymentOption) return null;
  const { context, id, gatewayName } = currentPaymentOption;
  return ({
    ...paramOrNul('payment id', id),
    ...paramOrNul('cc type', (context || {}).type),
    ...paramOrNul('payment gateway', gatewayName)
  });
};

const getChatPurchaseScreenName = ({ chatType }) => {
  switch (chatType) {
    case Const.chatType.text:
      return 'live chat';
    case Const.chatType.voice:
      return 'voice call';
    case Const.chatType.video:
      return 'video call';
    default:
      return null;
  }
};

const hidePaymentDetailsAndCancelChatIfNeed = ({
  errorType, advisorId, loadAdvisor, hidePaymentDetails, cancelChat
}) => {
  if (endPurchaseErrorTypes.includes(errorType)) {
    if (advisorId) loadAdvisor(getAdvisorSlug({ advisorId }));
    hidePaymentDetails();
    cancelChat();
  }
};

export const onRetryButtonClick = ({
  purchaseResponce, hidePaymentDetails, cancelChat, history, loadAdvisor, advisorId,
  purchaseURL, startPurchaseAction, clearPurchaseAction, clearAdditionalAction
}) => {
  const {
    errorOrigin, errorPathname, recoverable, retriable, errorType
  } = purchaseResponce;
  log(LOG_LEVELS.INFO, 'Purchase', 'Retry button clicked');
  log(LOG_LEVELS.INFO, 'Purchase', 'Retry button clicked', { recoverable, retriable });
  if (recoverable) {
    const recoverableParams = { recover: true };
    const recoverableURL = `${ errorOrigin }${ errorPathname }?${ queryString.stringify(recoverableParams) }`;
    startPurchaseAction(recoverableURL);
  } else if (retriable) {
    startPurchaseAction(purchaseURL);
  } else {
    clearPurchaseAction();
    clearAdditionalAction(false);
  }
  hidePaymentDetailsAndCancelChatIfNeed({
    errorType, advisorId, loadAdvisor, hidePaymentDetails, cancelChat
  });
  if (errorType === 'spending_limits') {
    window.onbeforeunload = null;
    history(appRoutes.spendLimits, { options: { state: { clickSource: 'popup' } } });
    window.location.reload();
  }
  log(LOG_LEVELS.INFO, 'Purchase', `retry Button click recoverable: ${ recoverable }, retriable: ${ retriable }`);
};

export const onConfirmButton = ({
  clearPurchaseAction, setRetryCount, onPurchaseComplete
}) => {
  clearPurchaseAction?.();
  setRetryCount(0);
  if (onPurchaseComplete) {
    onPurchaseComplete();
  }
};

export const payingWithCreditCard = ({ currentPaymentOption }) => {
  if (!currentPaymentOption) { return false; }
  return currentPaymentOption.kind === 'cc' || currentPaymentOption.kind === 'am';
};

const getPurchaseBillingAgreementProperty = ({
  currentPaymentOption, hasPaypalBillingAgreement
}) => {
  if (currentPaymentOption?.kind !== 'pp') return null;
  return { 'purchase billing agreement': hasPaypalBillingAgreement };
};

const set3DSAuthResult = (hash, threeDAuthResult) => {
  if (!threeDAuthResult) { return hash; }
  return {
    ...hash,
    'ThreeDS Auth Result': threeDAuthResult
  };
};

const extraEventParamsData = ({ extraEventParams }) => {
  if (extraEventParams && extraEventParams.length === 0) return {};
  return { ...toLowerCaseSpaced(extraEventParams) };
};

export const getPurchaseClickSource = (type, location) => {
  const mobileClickSource = (queryString.parse(window.location.search) || {}).click_source;
  if (mobileClickSource) return mobileClickSource;
  if (type === purchaseDetailsTypes.SIDE_MENU) return 'add funds';
  if (type === purchaseDetailsTypes.ADVISOR_BONUS) return 'bonus drawer';
  return getStartChatClickSource(location);
};

export const getPurchaseScreenProp = (type, chatType) => {
  const mobilePurchaseScreenProp = (queryString.parse(window.location.search) || {}).purchase_screen || (queryString.parse(window.location.search) || {}).click_source;
  return mobilePurchaseScreenProp || (type === purchaseDetailsTypes.SIDE_MENU ? 'credit screen' : getChatPurchaseScreenName({ chatType }));
};

const getPurchasesCountIfNeed = () => { // need for send purchases count to app for analytics
  if (!fromMobileApp) return {};
  const { user: { user } } = window.store.getState();
  return { 'purchases count': user.purchasesCount };
};

export const buildEventData = ({
  retryCount, type, purchaseAmount, bonus, hasPaypalBillingAgreement, threeDAuthResult,
  currentPaymentOption, chatType, purchaseFees, extraEventParams, location, clickSource = null, advisorId
}) => {
  const purchaseAmountFloat = parseFloat(purchaseAmount);
  return set3DSAuthResult({
    ...paramOrNul('advisor id', advisorId),
    'purchase screen': getPurchaseScreenProp(type, chatType),
    'click source': clickSource || getPurchaseClickSource(type, location),
    credit: (purchaseAmountFloat + parseFloat(bonus) ?? 0).toFixed(2),
    'base price': purchaseAmountFloat.toFixed(2),
    'purchase amount': purchaseAmountFloat.toFixed(2),
    'payment method': paymentKindToAnalyticsName(currentPaymentOption?.kind),
    ...getPurchaseBillingAgreementProperty({ currentPaymentOption, hasPaypalBillingAgreement }),
    'purchase source': fromMobileApp ? 'app' : 'web',
    'retry count': retryCount === undefined ? 0 : retryCount,
    ...paymentOptionEventData({ currentPaymentOption }),
    ...paramOrNul('purchase fees', purchaseFees || purchaseFees === 0 ? purchaseFees.toFixed(2) : null),
    ...extraEventParamsData({ extraEventParams }),
    ...getPurchasesCountIfNeed()
  }, threeDAuthResult);
};

export const mpTrackPurchaseStartedEvent = ({
  retryCount, conversionEventId, type, purchaseAmount, bonus, hasPaypalBillingAgreement,
  threeDAuthResult, currentPaymentOption, chatType, purchaseFees, location, advisorId
}) => {
  const purchaseData = buildEventData({
    retryCount, type, purchaseAmount, bonus, hasPaypalBillingAgreement, threeDAuthResult,
    currentPaymentOption, chatType, purchaseFees, location, advisorId
  });
  const userCurrency = window.store?.getState()?.user?.user?.currency || 'USD';
  const properties = {
    ...purchaseData,
    'local currency': userCurrency,
    ...getContinueSessionValue(),
    ...aditionalPurchaseProps()
  };
  analyticsService.trackEvent('purchase started', properties, { conversionEventId });
};

const purchaseEventAdditionalParams = (purchaseRes) => {
  const {
    paypalPayerId, paypalTransactionId, paypalBillingAgreementId, paypalInvoiceId,
    spendingLimits
  } = purchaseRes;

  const destination = getDestination({ spendingLimits });
  return ({
    ...paramOrNul('paypal payer id', paypalPayerId),
    ...paramOrNul('paypal transaction id', paypalTransactionId),
    ...paramOrNul('paypal billing agreement id', paypalBillingAgreementId),
    ...paramOrNul('paypal invoice id', paypalInvoiceId),
    ...paramOrNul('spending limits exceeded', spendingLimits),
    ...destination
  });
};

const getAdditonalPurchasePropsAndTrackEvent = (eventName, params, purchaseURL, getUser, conversionEventId, afterTrack) => {
  const trackPurchase = (extraParams) => {
    const allParams = { ...params, ...extraParams };
    analyticsService.trackEvent(eventName, allParams, { conversionEventId });
    if (afterTrack) afterTrack(allParams);
    if (getUser) getUser();
  };

  if (!purchaseURL) {
    log(LOG_LEVELS.DEBUG, 'Mixpanel', 'has no URL, tracking default params');
    trackPurchase();
    return;
  }
  getAdditionalPurchaseInfoFromURL(purchaseURL)
    .then((res) => {
      log(LOG_LEVELS.DEBUG, 'Mixpanel', 'got extra params, tracking with params');
      trackPurchase(purchaseEventAdditionalParams(res));
    }).catch(() => {
      log(LOG_LEVELS.DEBUG, 'Mixpanel', 'got extra params, tracking default params');
      trackPurchase();
    });
};

export const mpTrackPurchaseEvent = ({
  retryCount, conversionEventId, type, purchaseAmount, bonus, hasPaypalBillingAgreement,
  threeDAuthResult, currentPaymentOption, chatType, pathname, getUser, extraEventParams, location, advisorId
}) => {
  const userCurrency = window.store?.getState()?.user?.user?.currency || 'USD';
  const purchaseData = {
    ...buildEventData({
      retryCount, type, purchaseAmount, bonus, hasPaypalBillingAgreement, threeDAuthResult,
      currentPaymentOption, chatType, extraEventParams, location, advisorId
    }),
    'local currency': userCurrency
  };
  const price = purchaseData['base price'];
  const currency = purchaseData['local currency'];
  const eventName = 'Purchase';
  const properties = {
    ...purchaseData,
    price,
    currency,
    ...getContinueSessionValue(),
    'transaction total': purchaseData.credit,
    ...aditionalPurchaseProps()
  };

  const { user: { user: { purchasesCount } } } = window.store.getState();
  const afterTrack = purchasesCount === 0
    ? (allParams) => analyticsService.trackEvent('first purchase', allParams, { conversionEventId }, { conversionEventId, isCustomEvent: true })
    : null;

  getAdditonalPurchasePropsAndTrackEvent(eventName, properties, pathname, getUser, conversionEventId, afterTrack);
};

const getFailReason = ({ errorType, currentPaymentOption }) => {
  if (errorType) {
    return payPalProcessingFailReason(errorType);
  }
  if (currentPaymentOption?.kind === 'ap') {
    return 'apple pay failed';
  }
  if (currentPaymentOption?.kind === 'pp') {
    return 'paypal failed';
  }
  if (currentPaymentOption?.kind === 'cc') {
    return 'credit card failed';
  }
  if (currentPaymentOption?.kind === 'gp') {
    return 'google pay failed';
  }
  return 'error';
};

export const getPurchaseFailedPropsForMobileApp = ({
  type, purchaseAmount, bonus, currentPaymentOption, chatType, purchaseFees, extraEventParams, reason, errorType, errorMessage,
  errorName, errorCode, gatewayName, spendingLimits, location, advisorId
}) => ({
  ...buildEventData({
    type, purchaseAmount, bonus,
    currentPaymentOption, chatType, purchaseFees, extraEventParams, location, advisorId
  }),
  'failed reason': reason || getFailReason({ errorType, currentPaymentOption }),
  'payment error message': errorMessage !== undefined ? errorMessage : null,
  isCreditCard: payingWithCreditCard({ currentPaymentOption }),
  ...paramOrNul('failed reason description', errorName),
  ...paramOrNul('failed reason error code', errorCode),
  ...paramOrNul('payment gateway', gatewayName || currentPaymentOption?.gatewayName),
  ...paramOrNul('spending limits exceeded', spendingLimits),
  ...getDestination({ spendingLimits })
});

export const mpTrackPurchaseFailedEvent = ({
  errorType, errorMessage, errorName, errorCode, gatewayName, errorPathname, threeDAuthResult,
  retryCount, type, purchaseAmount, bonus, hasPaypalBillingAgreement, currentPaymentOption,
  chatType, reason, purchaseFees, extraEventParams, spendingLimits, location, topupFlow, clickSource = null, advisorId
}) => {
  const purchaseData = {
    ...buildEventData({
      retryCount, type, purchaseAmount, bonus, hasPaypalBillingAgreement, threeDAuthResult,
      currentPaymentOption, chatType, purchaseFees, extraEventParams, location, clickSource, advisorId
    }),
    'failed reason': reason || getFailReason({ errorType, currentPaymentOption }),
    'payment error message': errorMessage !== undefined ? errorMessage : null,
    isCreditCard: payingWithCreditCard({ currentPaymentOption }),
    ...paramOrNul('failed reason description', errorName),
    ...paramOrNul('failed reason error code', errorCode),
    ...paramOrNul('payment gateway', gatewayName || currentPaymentOption?.gatewayName),
    ...paramOrNul('spending limits exceeded', spendingLimits),
    ...getDestination({ spendingLimits }),
    ...paramOrNul('top up flow', topupFlow)
  };

  // const { extraEventParams } = purchaseData;
  const eventName = 'purchase failed';
  const properties = {
    ...getContinueSessionValue(),
    ...purchaseData,
    ...aditionalPurchaseProps()
  };
  getAdditonalPurchasePropsAndTrackEvent(eventName, properties, errorPathname);
};
